import React, { useState, useEffect } from 'react';
import { 
  Plus, Trash2, Save, Send, Eye, Image as ImageIcon, Type, AlignLeft, 
  Square, ChevronUp, ChevronDown, GripVertical, ArrowLeft, Upload, X, ChevronRight, List, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import RichTextEditor from './RichTextEditor';

const BRAND_COLORS = [
  { name: 'White', value: '#ffffff' },
  { name: 'Light Gray', value: '#f3f4f6' },
  { name: 'Teal Primary', value: '#00b48f' },
  { name: 'Dark Green', value: '#00461a' },
  { name: 'Light Teal', value: '#e6f7f4' },
  { name: 'Light Green', value: '#f0f7f4' },
];

const RoadmapPageBuilder = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  
  const [formData, setFormData] = useState({
    title: '',
    sections: []
  });

  const toggleSectionCollapse = (sectionIndex) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(sectionIndex)) {
      newCollapsed.delete(sectionIndex);
    } else {
      newCollapsed.add(sectionIndex);
    }
    setCollapsedSections(newCollapsed);
  };

  useEffect(() => {
    fetchCurrentPage();
  }, []);

  const fetchCurrentPage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/roadmap/page/current`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });
      const data = await response.json();
      
      if (data) {
        setPage(data);
        setFormData({
          title: data.title,
          sections: data.sections || []
        });
      }
    } catch (error) {
      console.error('Error fetching page:', error);
    }
  };

  // Section Management
  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      columns: [{ id: `col-${Date.now()}`, blocks: [] }],
      columnLayout: 1,
      backgroundColor: '#ffffff',
      padding: '20px'
    };
    setFormData({
      ...formData,
      sections: [...formData.sections, newSection]
    });
  };

  const deleteSection = (sectionIndex) => {
    const updatedSections = formData.sections.filter((_, i) => i !== sectionIndex);
    setFormData({ ...formData, sections: updatedSections });
  };

  const moveSectionUp = (sectionIndex) => {
    if (sectionIndex === 0) return;
    const updatedSections = [...formData.sections];
    [updatedSections[sectionIndex - 1], updatedSections[sectionIndex]] = 
    [updatedSections[sectionIndex], updatedSections[sectionIndex - 1]];
    setFormData({ ...formData, sections: updatedSections });
  };

  const moveSectionDown = (sectionIndex) => {
    if (sectionIndex === formData.sections.length - 1) return;
    const updatedSections = [...formData.sections];
    [updatedSections[sectionIndex], updatedSections[sectionIndex + 1]] = 
    [updatedSections[sectionIndex + 1], updatedSections[sectionIndex]];
    setFormData({ ...formData, sections: updatedSections });
  };

  const updateSectionLayout = (sectionIndex, layout) => {
    const updatedSections = [...formData.sections];
    const currentColumns = updatedSections[sectionIndex].columns.length;
    
    if (layout > currentColumns) {
      for (let i = currentColumns; i < layout; i++) {
        updatedSections[sectionIndex].columns.push({
          id: `col-${Date.now()}-${i}`,
          blocks: []
        });
      }
    } else if (layout < currentColumns) {
      updatedSections[sectionIndex].columns = updatedSections[sectionIndex].columns.slice(0, layout);
    }
    
    updatedSections[sectionIndex].columnLayout = layout;
    setFormData({ ...formData, sections: updatedSections });
  };

  const updateSectionColor = (sectionIndex, color) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].backgroundColor = color;
    setFormData({ ...formData, sections: updatedSections });
  };

  // Block Management
  const addBlock = (sectionIndex, columnIndex, blockType) => {
    const updatedSections = [...formData.sections];
    let newBlock;
    
    // Add unique ID to each block
    const blockId = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    switch (blockType) {
      case 'image':
        newBlock = { id: blockId, type: 'image', url: '', alt: '', link: '', width: '100%' };
        break;
      case 'imageWithText':
        newBlock = { 
          id: blockId, 
          type: 'imageWithText', 
          url: '', 
          alt: '', 
          link: '',
          imagePosition: 'left',
          content: '<p>Your text content here...</p>'
        };
        break;
      case 'heading':
        newBlock = { id: blockId, type: 'heading', level: 'h2', text: 'New Heading', color: '#000000' };
        break;
      case 'paragraph':
        newBlock = { id: blockId, type: 'paragraph', content: '<p>Your content here...</p>' };
        break;
      case 'button':
        newBlock = { id: blockId, type: 'button', text: 'Click Here', link: '#', backgroundColor: '#00b48f', textColor: '#ffffff', alignment: 'center' };
        break;
      case 'accordion':
        newBlock = { 
          id: blockId, 
          type: 'accordion', 
          title: 'Accordion Title', 
          content: '<p>Accordion content goes here...</p>', 
          titleColor: '#ffffff',
          titleBgColor: '#00b48f',
          contentColor: '#374151',
          contentBgColor: '#f3f4f6'
        };
        break;
      default:
        return;
    }
    
    updatedSections[sectionIndex].columns[columnIndex].blocks.push(newBlock);
    setFormData({ ...formData, sections: updatedSections });
  };

  const deleteBlock = (sectionIndex, columnIndex, blockIndex) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].columns[columnIndex].blocks.splice(blockIndex, 1);
    setFormData({ ...formData, sections: updatedSections });
  };

  const moveBlockUp = (sectionIndex, columnIndex, blockIndex) => {
    if (blockIndex === 0) return;
    const updatedSections = [...formData.sections];
    const blocks = updatedSections[sectionIndex].columns[columnIndex].blocks;
    [blocks[blockIndex - 1], blocks[blockIndex]] = [blocks[blockIndex], blocks[blockIndex - 1]];
    setFormData({ ...formData, sections: updatedSections });
  };

  const moveBlockDown = (sectionIndex, columnIndex, blockIndex) => {
    const updatedSections = [...formData.sections];
    const blocks = updatedSections[sectionIndex].columns[columnIndex].blocks;
    if (blockIndex === blocks.length - 1) return;
    [blocks[blockIndex], blocks[blockIndex + 1]] = [blocks[blockIndex + 1], blocks[blockIndex]];
    setFormData({ ...formData, sections: updatedSections });
  };

  const updateBlock = (sectionIndex, columnIndex, blockIndex, field, value) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].columns[columnIndex].blocks[blockIndex][field] = value;
    setFormData({ ...formData, sections: updatedSections });
  };

  const handleImageUpload = async (sectionIndex, columnIndex, blockIndex, file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletters/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      const data = await response.json();
      // Check if it's a data URL (base64) or a path
      const imageUrl = data.url.startsWith('data:') 
        ? data.url  // Already a complete data URL
        : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;  // Legacy path format
      updateBlock(sectionIndex, columnIndex, blockIndex, 'url', imageUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    }
  };

  const savePage = async () => {
    if (!formData.title) {
      toast.error('Please enter a page title');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (page) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/roadmap/page/${page.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          toast.success('Roadmap page saved successfully!');
          fetchCurrentPage();
        } else {
          const error = await response.json();
          toast.error(`Error: ${error.detail}`);
        }
      } else {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/roadmap/page/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          toast.success('Roadmap page created successfully!');
          fetchCurrentPage();
        } else {
          const error = await response.json();
          toast.error(`Error: ${error.detail}`);
        }
      }
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page');
    }
    setLoading(false);
  };

  const publishPage = async () => {
    if (!formData.title) {
      toast.error('Please enter a page title');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!page) {
        const createResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/roadmap/page/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
        
        if (!createResponse.ok) {
          const error = await createResponse.json();
          toast.error(`Error: ${error.detail}`);
          setLoading(false);
          return;
        }
        
        const createData = await createResponse.json();
        
        const publishResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/roadmap/page/${createData.page_id}/publish`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (publishResponse.ok) {
          toast.success('Roadmap page published successfully!');
          fetchCurrentPage();
        }
      } else {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/roadmap/page/${page.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        const publishResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/roadmap/page/${page.id}/publish`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (publishResponse.ok) {
          toast.success('Roadmap page published successfully!');
          fetchCurrentPage();
        } else {
          const error = await publishResponse.json();
          toast.error(`Error: ${error.detail}`);
        }
      }
    } catch (error) {
      console.error('Error publishing page:', error);
      toast.error('Failed to publish page');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin')}
                variant="ghost"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">2026 Roadmap Builder</h1>
                <p className="text-sm text-gray-500">Create and manage your company roadmap</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowPreview(!showPreview)}
                variant="outline"
                disabled={loading}
              >
                <Eye className="w-4 h-4 mr-2" />
                {showPreview ? 'Hide' : 'Show'} Preview
              </Button>
              <Button
                onClick={savePage}
                variant="outline"
                disabled={loading}
              >
                <Save className="w-4 h-4 mr-2" />
                Save Draft
              </Button>
              <Button
                onClick={publishPage}
                disabled={loading}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                Publish
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div>
        {/* Title Input */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Page Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., Our 2026 Roadmap"
            className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {formData.sections.map((section, sIndex) => (
            <div
              key={section.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Section Header */}
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleSectionCollapse(sIndex)}
                      className="p-1 rounded hover:bg-white transition-colors"
                      title={collapsedSections.has(sIndex) ? "Expand section" : "Collapse section"}
                    >
                      {collapsedSections.has(sIndex) ? (
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                      )}
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveSectionUp(sIndex)}
                        disabled={sIndex === 0}
                        className="p-1 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move section up"
                      >
                        <ChevronUp className="w-5 h-5 text-gray-600" />
                      </button>
                      <button
                        onClick={() => moveSectionDown(sIndex)}
                        disabled={sIndex === formData.sections.length - 1}
                        className="p-1 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move section down"
                      >
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                      </button>
                    </div>
                    <GripVertical className="w-5 h-5 text-gray-400" />
                    <span className="font-semibold text-gray-900">Section {sIndex + 1}</span>
                    {collapsedSections.has(sIndex) && (
                      <span className="text-xs text-gray-500 italic">
                        ({section.columns.reduce((sum, col) => sum + col.blocks.length, 0)} blocks)
                      </span>
                    )}
                  </div>
                  
                  <button
                    onClick={() => deleteSection(sIndex)}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                    title="Delete section"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Section Controls - Hidden when collapsed */}
              {!collapsedSections.has(sIndex) && (
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-4 flex-wrap">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Layout
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4].map((num) => (
                        <button
                          key={num}
                          onClick={() => updateSectionLayout(sIndex, num)}
                          className={`px-3 py-1.5 text-sm rounded-lg border-2 transition-all ${
                            section.columnLayout === num
                              ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        >
                          {num} Col
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Background
                    </label>
                    <div className="flex gap-2">
                      {BRAND_COLORS.map((color) => (
                        <button
                          key={color.value}
                          onClick={() => updateSectionColor(sIndex, color.value)}
                          className={`w-8 h-8 rounded-lg border-2 transition-all ${
                            section.backgroundColor === color.value
                              ? 'border-teal-500 scale-110'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Columns */}
              {!collapsedSections.has(sIndex) && (
              <div className={`grid gap-4 p-4 ${
                section.columnLayout === 1 ? 'grid-cols-1' :
                section.columnLayout === 2 ? 'grid-cols-2' :
                section.columnLayout === 3 ? 'grid-cols-3' :
                'grid-cols-4'
              }`}>
                {section.columns.map((column, cIndex) => (
                  <div key={column.id} className="space-y-3">
                    {/* Column Header */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500">Column {cIndex + 1}</span>
                      <div className="flex gap-1 flex-wrap">
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'image')}
                          className="p-1.5 rounded hover:bg-gray-100"
                          title="Add Image"
                        >
                          <ImageIcon className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'imageWithText')}
                          className="p-1.5 rounded hover:bg-gray-100"
                          title="Add Image with Text"
                        >
                          <FileText className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'heading')}
                          className="p-1.5 rounded hover:bg-gray-100"
                          title="Add Heading"
                        >
                          <Type className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'paragraph')}
                          className="p-1.5 rounded hover:bg-gray-100"
                          title="Add Rich Text"
                        >
                          <AlignLeft className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'button')}
                          className="p-1.5 rounded hover:bg-gray-100"
                          title="Add Button"
                        >
                          <Square className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => addBlock(sIndex, cIndex, 'accordion')}
                          className="p-1.5 rounded hover:bg-gray-100"
                          title="Add Accordion"
                        >
                          <List className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    </div>

                    {/* Blocks */}
                    <div className="space-y-3">
                      {column.blocks.map((block, bIndex) => {
                        // Generate stable key - use block.id if exists, otherwise create one
                        const blockKey = block.id || `${sIndex}-${cIndex}-${bIndex}`;
                        
                        return (
                        <div
                          key={blockKey}
                          className="bg-white border-2 border-gray-200 rounded-lg p-3 hover:border-teal-300 transition-colors"
                        >
                          {/* Block Controls */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="flex gap-0.5">
                                <button
                                  onClick={() => moveBlockUp(sIndex, cIndex, bIndex)}
                                  disabled={bIndex === 0}
                                  className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                                  title="Move up"
                                >
                                  <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                                </button>
                                <button
                                  onClick={() => moveBlockDown(sIndex, cIndex, bIndex)}
                                  disabled={bIndex === column.blocks.length - 1}
                                  className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                                  title="Move down"
                                >
                                  <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                                </button>
                              </div>
                              <span className="text-xs font-medium text-gray-600 capitalize">
                                {block.type}
                              </span>
                            </div>
                            <button
                              onClick={() => deleteBlock(sIndex, cIndex, bIndex)}
                              className="p-1 rounded hover:bg-red-50 text-red-600"
                              title="Delete block"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Block Content Based on Type */}
                          {block.type === 'image' && (
                            <div className="space-y-2">
                              {block.url ? (
                                <div className="relative">
                                  <img src={block.url} alt={block.alt} className="w-full rounded" />
                                  <button
                                    onClick={() => updateBlock(sIndex, cIndex, bIndex, 'url', '')}
                                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 transition-colors">
                                  <div className="text-center">
                                    <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                                    <span className="text-xs text-gray-500">Upload Image</span>
                                  </div>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleImageUpload(sIndex, cIndex, bIndex, e.target.files[0])}
                                  />
                                </label>
                              )}
                              <input
                                type="text"
                                value={block.alt || ''}
                                onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'alt', e.target.value)}
                                placeholder="Alt text"
                                className="w-full px-2 py-1 text-xs border rounded"
                              />
                              <input
                                type="text"
                                value={block.link || ''}
                                onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'link', e.target.value)}
                                placeholder="Link URL (optional)"
                                className="w-full px-2 py-1 text-xs border rounded"
                              />
                            </div>
                          )}

                          {block.type === 'imageWithText' && (
                            <div className="space-y-2">
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Image Position</label>
                                <select
                                  value={block.imagePosition || 'left'}
                                  onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'imagePosition', e.target.value)}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                >
                                  <option value="left">Image on Left</option>
                                  <option value="right">Image on Right</option>
                                </select>
                              </div>
                              {block.url ? (
                                <div className="relative">
                                  <img src={block.url} alt={block.alt} className="w-full rounded" />
                                  <button
                                    onClick={() => updateBlock(sIndex, cIndex, bIndex, 'url', '')}
                                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 transition-colors">
                                  <div className="text-center">
                                    <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                                    <span className="text-xs text-gray-500">Upload Image</span>
                                  </div>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleImageUpload(sIndex, cIndex, bIndex, e.target.files[0])}
                                  />
                                </label>
                              )}
                              <input
                                type="text"
                                value={block.alt || ''}
                                onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'alt', e.target.value)}
                                placeholder="Alt text"
                                className="w-full px-2 py-1 text-xs border rounded"
                              />
                              <input
                                type="text"
                                value={block.link || ''}
                                onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'link', e.target.value)}
                                placeholder="Link URL (optional)"
                                className="w-full px-2 py-1 text-xs border rounded"
                              />
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Text Content</label>
                                <RichTextEditor
                                  value={block.content || ''}
                                  onChange={(value) => updateBlock(sIndex, cIndex, bIndex, 'content', value)}
                                  placeholder="Enter text content..."
                                />
                              </div>
                            </div>
                          )}

                          {block.type === 'heading' && (
                            <div className="space-y-2">
                              <select
                                value={block.level}
                                onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'level', e.target.value)}
                                className="w-full px-2 py-1 text-xs border rounded"
                              >
                                <option value="h1">Heading 1</option>
                                <option value="h2">Heading 2</option>
                                <option value="h3">Heading 3</option>
                              </select>
                              <input
                                type="text"
                                value={block.text}
                                onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'text', e.target.value)}
                                placeholder="Heading text"
                                className="w-full px-2 py-1 text-sm border rounded"
                              />
                            </div>
                          )}

                          {block.type === 'paragraph' && (
                            <div className="space-y-2">
                              <RichTextEditor
                                value={block.content}
                                onChange={(value) => updateBlock(sIndex, cIndex, bIndex, 'content', value)}
                                placeholder="Enter your content..."
                              />
                            </div>
                          )}

                          {block.type === 'button' && (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={block.text || ''}
                                onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'text', e.target.value)}
                                placeholder="Button text"
                                className="w-full px-2 py-1 text-xs border rounded"
                              />
                              <input
                                type="text"
                                value={block.link || ''}
                                onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'link', e.target.value)}
                                placeholder="Button URL"
                                className="w-full px-2 py-1 text-xs border rounded"
                              />
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Button Color</label>
                                  <input
                                    type="color"
                                    value={block.backgroundColor || '#00b48f'}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'backgroundColor', e.target.value)}
                                    className="w-full h-8 rounded border"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Text Color</label>
                                  <input
                                    type="color"
                                    value={block.textColor || '#ffffff'}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'textColor', e.target.value)}
                                    className="w-full h-8 rounded border"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-1 flex-wrap items-center">
                                <span className="text-xs text-gray-500">Brand colors:</span>
                                {BRAND_COLORS.map((color) => (
                                  <button
                                    key={color.value}
                                    onClick={() => updateBlock(sIndex, cIndex, bIndex, 'backgroundColor', color.value)}
                                    className="w-6 h-6 rounded border-2 border-gray-300 hover:border-teal-500"
                                    style={{ backgroundColor: color.value }}
                                    title={`Set to ${color.name}`}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {block.type === 'accordion' && (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={block.title || ''}
                                onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'title', e.target.value)}
                                placeholder="Accordion title"
                                className="w-full px-2 py-1 text-sm font-medium border rounded"
                              />
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Content</label>
                                <RichTextEditor
                                  value={block.content || ''}
                                  onChange={(value) => updateBlock(sIndex, cIndex, bIndex, 'content', value)}
                                  placeholder="Enter accordion content..."
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Title Text</label>
                                  <input
                                    type="color"
                                    value={block.titleColor || '#ffffff'}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'titleColor', e.target.value)}
                                    className="w-full h-8 rounded border"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Title BG</label>
                                  <input
                                    type="color"
                                    value={block.titleBgColor || '#00b48f'}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'titleBgColor', e.target.value)}
                                    className="w-full h-8 rounded border"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Content Text</label>
                                  <input
                                    type="color"
                                    value={block.contentColor || '#374151'}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'contentColor', e.target.value)}
                                    className="w-full h-8 rounded border"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Content BG</label>
                                  <input
                                    type="color"
                                    value={block.contentBgColor || '#f3f4f6'}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, 'contentBgColor', e.target.value)}
                                    className="w-full h-8 rounded border"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-1 flex-wrap items-center">
                                <span className="text-xs text-gray-500">Quick title BG:</span>
                                {BRAND_COLORS.map((color) => (
                                  <button
                                    key={color.value}
                                    onClick={() => updateBlock(sIndex, cIndex, bIndex, 'titleBgColor', color.value)}
                                    className="w-6 h-6 rounded border-2 border-gray-300 hover:border-teal-500"
                                    style={{ backgroundColor: color.value }}
                                    title={`Set title background to ${color.name}`}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        );
                      })}

                      {column.blocks.length === 0 && (
                        <div className="text-center py-6 text-gray-400 text-xs border-2 border-dashed border-gray-200 rounded-lg">
                          Add blocks using the icons above
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Section Button */}
        <button
          onClick={addSection}
          className="w-full mt-6 py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-colors group"
        >
          <div className="flex items-center justify-center gap-2 text-gray-600 group-hover:text-teal-600">
            <Plus className="w-5 h-5" />
            <span className="font-medium">Add New Section</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default RoadmapPageBuilder;
